import type { ArrivalTimePreference, DepartureTimePreference, DepartureTimeSlot } from '@/src/types/aiRecommendation'

const MINUTES_IN_DAY = 1440
const DEFAULT_FLEXIBILITY = 30
const DECAY_MULTIPLIER = 2.5

// ── Legacy bucket-based scoring (used when no time preference exists) ──

export function getDepartureSlot(time: string | undefined): DepartureTimeSlot | null {
    if (!time) return null
    const numeric = Number(time.replace(':', '').slice(0, 4))
    if (numeric >= 0 && numeric < 600) return '0600'
    if (numeric >= 600 && numeric < 1200) return '1200'
    if (numeric >= 1200 && numeric < 1800) return '1800'
    if (numeric >= 1800 && numeric < 2400) return '2000'
    return null
}

export function computeDepartureScore(slot: DepartureTimeSlot | null, preferredSlot?: DepartureTimeSlot | null): number {
    if (!slot) return 0.5
    if (preferredSlot && slot === preferredSlot) return 1
    if (preferredSlot) return 0.6

    if (slot === '1200' || slot === '1800') return 1
    if (slot === '2000') return 0.7
    return 0.6
}

// ── Time-distance scoring (used when AI returns departure time preference) ──

export function parseTimeToMinutes(time: string | undefined): number | null {
    if (!time) return null
    const parts = time.split(':')
    if (parts.length < 2) return null
    const h = parseInt(parts[0], 10)
    const m = parseInt(parts[1], 10)
    if (!Number.isFinite(h) || !Number.isFinite(m)) return null
    return ((h * 60) + m) % MINUTES_IN_DAY
}

function circularDistance(a: number, b: number): number {
    const diff = Math.abs(a - b)
    return Math.min(diff, MINUTES_IN_DAY - diff)
}

function isInTimeRange(timeMin: number, startMin: number, endMin: number): boolean {
    if (startMin <= endMin) {
        return timeMin >= startMin && timeMin <= endMin
    }
    // wraps midnight (e.g. 23:00 → 02:00)
    return timeMin >= startMin || timeMin <= endMin
}

export interface TimeDistanceResult {
    score: number
    isExactMatch: boolean
}

/**
 * Continuous time-distance scoring with buffer decay.
 *
 * In core range: score = 1.0, isExactMatch = true
 * Outside core: linear decay over (flexibility * 2.5) minutes, isExactMatch = false
 *
 * Example with flexibility=30 and core 02:00–05:00:
 *   3:00 AM → 1.0    (in core)
 *   5:15 AM → 0.8    (15 min from edge, 15/75 = 0.2 penalty)
 *   5:30 AM → 0.6    (30 min from edge)
 *   6:00 AM → 0.2    (60 min from edge)
 *   6:15 AM → 0.0    (75 min from edge = full decay)
 */
export function computeTimeDistanceScore(
    flightTime: string | undefined,
    preference: DepartureTimePreference
): TimeDistanceResult {
    const flightMin = parseTimeToMinutes(flightTime)
    const startMin = parseTimeToMinutes(preference.start)
    const endMin = parseTimeToMinutes(preference.end)

    if (flightMin === null || startMin === null || endMin === null) {
        return { score: 0.5, isExactMatch: false }
    }

    if (isInTimeRange(flightMin, startMin, endMin)) {
        return { score: 1.0, isExactMatch: true }
    }

    const flexibility = preference.flexibility ?? DEFAULT_FLEXIBILITY
    const decayWindow = flexibility * DECAY_MULTIPLIER

    const distToStart = circularDistance(flightMin, startMin)
    const distToEnd = circularDistance(flightMin, endMin)
    const distance = Math.min(distToStart, distToEnd)

    const score = Math.max(0, 1.0 - distance / decayWindow)

    return { score, isExactMatch: false }
}

// ── Arrival time scoring (mirrors departure time, operates on lastArrival.time) ──

export const getArrivalSlot = getDepartureSlot

export const computeArrivalScore = computeDepartureScore

export function computeArrivalTimeDistanceScore(
    flightArrivalTime: string | undefined,
    preference: ArrivalTimePreference
): TimeDistanceResult {
    return computeTimeDistanceScore(flightArrivalTime, preference)
}
